<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 class="text-xl font-bold text-gray-900">
          Adjust Used Days
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <X class="w-6 h-6" />
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <!-- Employee Info -->
        <div class="bg-blue-50 p-4 rounded-md">
          <p class="text-sm font-medium text-gray-700">Employee</p>
          <p class="text-lg font-semibold text-gray-900">{{ employeeName }}</p>
          <p class="text-sm text-gray-600">{{ bankName }}</p>
          <p class="text-xs text-gray-500 mt-2">
            Current Used Days: <span class="font-semibold">{{ currentUsedDays }}</span>
          </p>
        </div>

        <!-- New Used Days -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            New Used Days *
          </label>
          <input
            v-model.number="formData.used_days"
            type="number"
            step="0.5"
            min="0"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter new used days value"
          />
          <p class="text-xs text-gray-500 mt-1">
            <span v-if="changeAmount !== null" :class="changeAmountClass">
              {{ changeAmountText }}
            </span>
          </p>
        </div>

        <!-- Reason -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Reason for Adjustment
          </label>
          <textarea
            v-model="formData.reason"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Explain why you're adjusting the used days..."
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">
            This will be recorded in the audit history
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="loading || formData.used_days === null"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? 'Adjusting...' : 'Adjust Used Days' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { X } from 'lucide-vue-next';

export default {
  name: 'AdjustUsedDaysModal',
  components: {
    X
  },
  props: {
    employeeId: {
      type: Number,
      required: true
    },
    employeeName: {
      type: String,
      required: true
    },
    bankId: {
      type: Number,
      required: true
    },
    bankName: {
      type: String,
      required: true
    },
    currentUsedDays: {
      type: Number,
      required: true
    },
    year: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      formData: {
        used_days: this.currentUsedDays,
        reason: ''
      }
    };
  },
  computed: {
    changeAmount() {
      if (this.formData.used_days === null || this.formData.used_days === '') {
        return null;
      }
      return this.formData.used_days - this.currentUsedDays;
    },
    changeAmountText() {
      if (this.changeAmount === null) return '';
      if (this.changeAmount === 0) return 'No change';
      if (this.changeAmount > 0) {
        return `+${this.changeAmount.toFixed(1)} days (reduces available balance)`;
      }
      return `${this.changeAmount.toFixed(1)} days (increases available balance)`;
    },
    changeAmountClass() {
      if (this.changeAmount === null || this.changeAmount === 0) {
        return 'text-gray-600';
      }
      return this.changeAmount > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium';
    }
  },
  methods: {
    handleSubmit() {
      if (this.formData.used_days === null || this.formData.used_days < 0) {
        return;
      }

      this.$emit('submit', {
        employeeId: this.employeeId,
        bankId: this.bankId,
        year: this.year,
        used_days: this.formData.used_days,
        reason: this.formData.reason || 'Manual adjustment by admin'
      });
    }
  }
};
</script>
